// Module-level singleton registry — ChatOverlay consults `isCheatConsoleOpen()`
// before claiming Enter so cheat commands take priority. Kept as a registry
// rather than a DOM sniff so the priority contract is explicit and testable.
let active: CheatConsole | null = null;

export function isCheatConsoleOpen(): boolean {
  return active?.isOpen() ?? false;
}

export class CheatConsole {
  private panel: HTMLDivElement;
  private output: HTMLDivElement;
  private input: HTMLInputElement;
  private visible = false;
  private readonly keyHandler: (e: KeyboardEvent) => void;

  constructor(
    private readonly game: { canvas: HTMLCanvasElement; config: { width: unknown; height: unknown }; input: { keyboard: { disableGlobalCapture?: () => void; enableGlobalCapture?: () => void } | null } },
    private readonly onSubmit?: (cmd: string) => void,
  ) {
    active = this;
    // Build panel
    this.panel = document.createElement('div');
    this.panel.setAttribute('data-cheat-panel', '');
    Object.assign(this.panel.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      height: '180px',
      background: 'rgba(10, 8, 15, 0.92)',
      borderBottom: '1px solid #4ade80',
      display: 'flex',
      flexDirection: 'column',
      zIndex: '99998',
      fontFamily: 'monospace',
      fontSize: '13px',
      transform: 'translateY(-100%)',
      transition: 'transform 0.2s ease',
    });

    this.output = document.createElement('div');
    this.output.setAttribute('data-cheat-output', '');
    Object.assign(this.output.style, {
      flex: '1',
      overflowY: 'auto',
      padding: '6px 10px',
      color: '#a0ff80',
    });

    this.input = document.createElement('input');
    this.input.setAttribute('data-cheat-input', '');
    this.input.type = 'text';
    this.input.placeholder = 'Enter command (e.g. /gold set 9999)…';
    Object.assign(this.input.style, {
      background: '#0d0a0e',
      color: '#e2e8f0',
      border: 'none',
      borderTop: '1px solid #374151',
      padding: '6px 10px',
      outline: 'none',
      fontFamily: 'monospace',
      fontSize: '13px',
    });

    this.input.addEventListener('focus', () => {
      this.game.input.keyboard?.disableGlobalCapture?.();
    });
    this.input.addEventListener('blur', () => {
      this.game.input.keyboard?.enableGlobalCapture?.();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const cmd = this.input.value.trim();
        this.input.value = '';
        if (cmd) this.onSubmit?.(cmd);
      }
    });

    this.panel.appendChild(this.output);
    this.panel.appendChild(this.input);
    document.body.appendChild(this.panel);

    // Toggle on backtick / ² (AZERTY) — capture phase so it fires before Phaser
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.code === 'Backquote' || e.key === '²' || e.code === 'ShiftRight') {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      }
    };
    document.addEventListener('keydown', this.keyHandler, true);
  }

  log(message: string, color = '#a0ff80'): void {
    const line = document.createElement('div');
    line.style.color = color;
    line.textContent = message;
    this.output.appendChild(line);
    this.output.scrollTop = this.output.scrollHeight;
  }

  isOpen(): boolean {
    return this.visible;
  }

  private toggle(): void {
    this.visible = !this.visible;
    this.panel.style.transform = this.visible ? 'translateY(0)' : 'translateY(-100%)';
    if (this.visible) {
      setTimeout(() => this.input.focus(), 0);
    }
  }

  destroy(): void {
    document.removeEventListener('keydown', this.keyHandler, true);
    this.panel.remove();
    if (active === this) active = null;
  }
}
